import { ControlCodes } from '@bcube/shared/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { Clock, Info, Loader2, Save, Undo2, Users as UsersIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBlocker } from 'react-router';

import { type Audit, auditApi } from '@/api/admin/audit';
import type { Control } from '@/api/admin/controls';
import { type Profile, profileApi } from '@/api/admin/profiles';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '@/context/auth-context';
import { useToast } from '@/hooks/use-toast';
import { CONTROLS_TREE, type ControlNode } from '@/lib/controls-definition';
import { findSubtree, isAncestorToggleDisabled } from '@/lib/controls-utils';
import { cn } from '@/lib/utils';
import { ProfileSelector } from './profile-selector';
import { useLockedControlDraft } from './use-locked-control-draft';

// Hook for managing profile linked modal
function useProfileLinkedModal() {
  const [open, setOpen] = useState(false);
  const [selectedControl, setSelectedControl] = useState<Pick<
    Control,
    'id' | 'name' | 'code'
  > | null>(null);

  const { data: profiles, isLoading } = useQuery({
    queryKey: ['profiles-by-control', selectedControl?.code],
    queryFn: () => profileApi.findByControlCode(selectedControl?.code!),
    enabled: !!selectedControl?.code && open,
  });

  const handleOpen = useCallback((control: Pick<Control, 'id' | 'name' | 'code'>) => {
    setSelectedControl(control);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return {
    open,
    control: selectedControl,
    profiles: profiles ?? { docs: [], total: 0, page: 1, pages: 1, limit: 10 },
    isLoading,
    handleOpen,
    handleClose,
  };
}

// Hook for audit modal (control changes)
function useControlAuditModal() {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedControl, setSelectedControl] = useState<Pick<
    Control,
    'id' | 'name' | 'code'
  > | null>(null);

  const { data: audits, isLoading } = useQuery({
    queryKey: ['audit-by-control', selectedControl?.code, page],
    queryFn: () => auditApi.findByControlCode(selectedControl?.code!, { page, limit: 10 }),
    enabled: !!selectedControl?.code && open,
  });

  const handleOpen = useCallback((control: Pick<Control, 'id' | 'name' | 'code'>) => {
    setSelectedControl(control);
    setPage(1);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  return {
    open,
    control: selectedControl,
    audits: audits ?? { docs: [], total: 0, page: 1, pages: 1, limit: 10 },
    isLoading,
    currentPage: page,
    handleOpen,
    handleClose,
    handlePageChange,
  };
}

// Profile linked modal component
function ProfileLinkedModal({
  open,
  onClose,
  control,
  profiles,
  isLoading,
}: {
  open: boolean;
  onClose: () => void;
  control: Pick<Control, 'id' | 'name'> | null;
  profiles: { docs: Profile[] };
  isLoading: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Perfis relacionados ao controle de acesso - {control?.name}</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }, (_, i) => `profile-skeleton-${i}`).map((key) => (
                <Skeleton key={key} className="h-12 w-full" />
              ))}
            </div>
          ) : profiles.docs.length === 0 ? (
            <p className="text-center text-gray-500 py-4">
              Nenhum perfil vinculado a este controle.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome do Perfil</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {profiles.docs.map((profile) => (
                  <TableRow key={profile.id}>
                    <TableCell className="font-medium">{profile.name}</TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2 py-1 text-xs font-medium',
                          profile.active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700',
                        )}
                      >
                        {profile.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper function to format audit action
function formatAuditAction(audit: Audit): React.ReactNode {
  const { action, metadata, type } = audit;

  if (type === 'create' || !metadata || metadata.length === 0) {
    return action;
  }

  const label = action.split("'")[1] ?? 'registro';
  const permissionMeta = metadata.find((m) => m.field === 'locked_control_code');

  if (permissionMeta) {
    const before = Array.isArray(permissionMeta.before) ? permissionMeta.before : [];
    const after = Array.isArray(permissionMeta.after) ? permissionMeta.after : [];
    const beforeSet = new Set(before);
    const afterSet = new Set(after);
    const added = after.filter((x: string) => !beforeSet.has(x));
    const removed = before.filter((x: string) => !afterSet.has(x));

    return (
      <div className="space-y-1 text-sm">
        <div>{`Permissões de '${label}' alteradas`}</div>
        {added.length > 0 && (
          <div>
            <span className="text-green-600 font-medium mr-1">Adicionadas:</span>
            <span className="inline-flex flex-wrap gap-1">
              {added.slice(0, 3).map((code: string) => (
                <span
                  key={code}
                  className="inline-flex items-center rounded-full px-2 py-0.5 text-xs bg-green-100"
                  title={code}
                >
                  {code.split(':').pop()}
                </span>
              ))}
              {added.length > 3 && (
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs bg-gray-100">
                  +{added.length - 3} mais
                </span>
              )}
            </span>
          </div>
        )}
        {removed.length > 0 && (
          <div>
            <span className="text-red-600 font-medium mr-1">Removidas:</span>
            <span className="inline-flex flex-wrap gap-1">
              {removed.slice(0, 3).map((code: string) => (
                <span
                  key={code}
                  className="inline-flex items-center rounded-full px-2 py-0.5 text-xs bg-red-100"
                  title={code}
                >
                  {code.split(':').pop()}
                </span>
              ))}
              {removed.length > 3 && (
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs bg-gray-100">
                  +{removed.length - 3} mais
                </span>
              )}
            </span>
          </div>
        )}
      </div>
    );
  }

  return action;
}

// Control audit modal component
function ControlAuditModal({
  open,
  onClose,
  control,
  audits,
  isLoading,
  currentPage,
  onPageChange,
}: {
  open: boolean;
  onClose: () => void;
  control: Pick<Control, 'id' | 'name'> | null;
  audits: { docs: Audit[]; total: number; pages: number; limit: number };
  isLoading: boolean;
  currentPage: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Histórico de alterações de perfis com o módulo - {control?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4 flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex justify-center items-center h-48">
              <div className="flex flex-col items-center gap-3">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
                <p className="text-sm text-gray-600">Carregando dados...</p>
              </div>
            </div>
          ) : audits.docs.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              Nenhum registro de alteração encontrado para este controle.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[150px]">Data/Hora</TableHead>
                  <TableHead className="w-[150px]">Usuário</TableHead>
                  <TableHead>Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audits.docs.map((audit) => (
                  <TableRow key={audit.id}>
                    <TableCell className="text-sm">
                      {new Intl.DateTimeFormat('pt-BR', {
                        dateStyle: 'short',
                        timeStyle: 'medium',
                      }).format(new Date(audit.created_at))}
                    </TableCell>
                    <TableCell className="text-sm">{audit.user?.name ?? '-'}</TableCell>
                    <TableCell>{formatAuditAction(audit)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        {audits.docs.length > 0 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={audits.pages || 1}
              totalItems={audits.total}
              limit={audits.limit}
              onPageChange={onPageChange}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Set locked profile form (toggle switch) — reads/writes from draft state
function SetLockedProfileForm({
  node,
  draftCodes,
  toggleCode,
  isDisabledByCascade,
}: {
  node: ControlNode;
  draftCodes: Set<string>;
  toggleCode: (code: string, node: ControlNode) => void;
  isDisabledByCascade: boolean;
}) {
  const isLinked = draftCodes.has(node.code);

  return (
    <Switch
      checked={isLinked}
      onCheckedChange={() => toggleCode(node.code, node)}
      disabled={isDisabledByCascade}
      title={
        isDisabledByCascade
          ? 'Desabilitado - permissão pai está desativada'
          : isLinked
            ? 'Remover acesso'
            : 'Conceder acesso'
      }
    />
  );
}

// Control tree item renderer using static ControlNode - flat compact layout
function ControlTreeItem({
  node,
  draftCodes,
  toggleCode,
  onOpenProfileModal,
  onOpenAuditModal,
  hasUpdatePermission,
  depth = 0,
}: {
  node: ControlNode;
  draftCodes: Set<string>;
  toggleCode: (code: string, node: ControlNode) => void;
  onOpenProfileModal: (control: Pick<Control, 'id' | 'name' | 'code'>) => void;
  onOpenAuditModal: (control: Pick<Control, 'id' | 'name' | 'code'>) => void;
  hasUpdatePermission: boolean;
  depth?: number;
}) {
  const hasChildren = (node.children?.length ?? 0) > 0;

  // Check if this control is disabled due to a parent toggle being off (using draftCodes)
  const isDisabledByCascade = isAncestorToggleDisabled(node.code, draftCodes);

  // Indentation padding for nested items
  const indentPadding = depth > 0 ? { paddingLeft: `${depth * 1.5}rem` } : {};

  return (
    <>
      <div
        className={cn(
          'border-b border-gray-200 w-full py-3 px-4 flex justify-between items-center gap-2',
          isDisabledByCascade && 'opacity-50 bg-gray-50',
        )}
      >
        {/* Left side: Switch + Name (with indentation) */}
        <div className="flex items-center gap-3" style={indentPadding}>
          {node.isToggle && hasUpdatePermission && (
            <SetLockedProfileForm
              node={node}
              draftCodes={draftCodes}
              toggleCode={toggleCode}
              isDisabledByCascade={isDisabledByCascade}
            />
          )}
          {node.isToggle && !hasUpdatePermission && (
            <div className="w-9" /> // Placeholder for alignment
          )}
          {!node.isToggle && <div className="w-9" />}
          <span
            className={cn(
              'text-sm',
              hasChildren && node.isToggle && 'font-semibold',
              !node.isToggle && 'text-gray-600',
            )}
          >
            {node.name}
          </span>
          {node.isToggle && node.description && (
            <span title={node.description}>
              <Info className="h-4 w-4 text-gray-400" />
            </span>
          )}
        </div>

        {/* Right side: Action buttons */}
        {node.isToggle && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              title="Histórico"
              onClick={() =>
                onOpenAuditModal({
                  id: node.code,
                  name: node.name,
                  code: node.code,
                })
              }
              className="h-7 w-7 p-0"
            >
              <Clock className="h-4 w-4 text-gray-500" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              title="Perfis"
              onClick={() =>
                onOpenProfileModal({
                  id: node.code,
                  name: node.name,
                  code: node.code,
                })
              }
              className="h-7 w-7 p-0"
            >
              <UsersIcon className="h-4 w-4 text-gray-500" />
            </Button>
          </div>
        )}
      </div>

      {/* Render children flat (always visible) */}
      {hasChildren &&
        node.children?.map((child) => (
          <ControlTreeItem
            key={child.code}
            node={child}
            draftCodes={draftCodes}
            toggleCode={toggleCode}
            onOpenProfileModal={onOpenProfileModal}
            onOpenAuditModal={onOpenAuditModal}
            hasUpdatePermission={hasUpdatePermission}
            depth={depth + 1}
          />
        ))}
    </>
  );
}

export function ControlDetailView({
  profileId,
  onProfileChange,
}: {
  profileId: string;
  onProfileChange: (profileId: string) => void;
}) {
  const { restrictFeatureByProfile, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [activeModule, setActiveModule] = useState(CONTROLS_TREE[0]?.code ?? '');

  const profileLinkedModal = useProfileLinkedModal();
  const controlAuditModal = useControlAuditModal();

  // Fetch selected profile details
  const { data: selectedProfile } = useQuery({
    queryKey: ['profile', profileId],
    queryFn: () => profileApi.findOne(profileId),
    enabled: !!profileId,
  });

  const hasUpdatePermission = restrictFeatureByProfile(ControlCodes.admin_control_update);

  // Draft hook: local state for toggle changes (no API calls on toggle)
  const { draftCodes, toggleCode, computeDiff, isDirty, reset } =
    useLockedControlDraft(selectedProfile);

  // --- Navigation guard: beforeunload for browser close/refresh ---
  useEffect(() => {
    if (!isDirty) return;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // --- Navigation guard: useBlocker for in-app navigation ---
  const blocker = useBlocker(isDirty);

  // --- Profile change guard: confirm before switching profile when dirty ---
  const [pendingProfileId, setPendingProfileId] = useState<string | null>(null);

  const handleProfileChange = useCallback(
    (newProfileId: string) => {
      if (isDirty) {
        setPendingProfileId(newProfileId);
      } else {
        onProfileChange(newProfileId);
      }
    },
    [isDirty, onProfileChange],
  );

  // Save mutation: computes diff and fires sequential API calls (add then remove)
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedProfile) throw new Error('Nenhum perfil selecionado');

      const { toAdd, toRemove } = computeDiff();

      if (toAdd.length > 0) {
        await profileApi.setLockedControls(selectedProfile.id, {
          locked_control_code: toAdd,
          operation: 'add',
          control_name: activeModule,
        });
      }

      if (toRemove.length > 0) {
        await profileApi.setLockedControls(selectedProfile.id, {
          locked_control_code: toRemove,
          operation: 'remove',
          control_name: activeModule,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['profiles-by-control'] });
      refreshProfile();
      toast({
        title: 'Sucesso',
        description: 'Permissões atualizadas com sucesso.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro',
        description: error instanceof Error ? error.message : 'Falha ao salvar permissões.',
        variant: 'destructive',
      });
    },
  });

  // Build change summary text for the sticky bar
  const changeSummary = useMemo(() => {
    if (!isDirty) return '';
    const { toAdd, toRemove } = computeDiff();
    const parts: string[] = [];
    if (toAdd.length > 0) parts.push(`+${toAdd.length} adicionados`);
    if (toRemove.length > 0) parts.push(`-${toRemove.length} removidos`);
    return parts.join(', ');
  }, [isDirty, computeDiff]);

  // Get the static control tree from definition using the active module code
  const controlTree = useMemo(() => {
    if (!activeModule) return null;
    return findSubtree(activeModule);
  }, [activeModule]);

  return (
    <div className={isDirty ? 'pb-20' : ''}>
      {/* Profile Selector (with dirty guard) */}
      <div className="flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
        <div className="w-full ring-1 ring-inset ring-gray-900/10 rounded-md shadow-sm bg-white py-2 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8">
          <ProfileSelector profileId={profileId} onProfileChange={handleProfileChange} />
        </div>
      </div>

      {/* Module quick-nav tabs */}
      <nav aria-label="Módulos" className="mt-10 px-4 sm:px-6 lg:px-8">
        <div className="flex gap-1 overflow-x-auto border-b border-gray-200">
          {CONTROLS_TREE.map((module) => {
            const isActive = module.code === activeModule;
            return (
              <button
                key={module.code}
                type="button"
                onClick={() => setActiveModule(module.code)}
                className={cn(
                  'whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer',
                  isActive
                    ? 'border-gray-900 text-gray-900 bg-gray-100'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                )}
              >
                {module.name}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Controls Tree */}
      <ul
        aria-label="Controles de acesso"
        className="mt-10 px-4 sm:px-6 lg:px-8 flex flex-col space-y-2"
      >
        {!controlTree || !controlTree.children || controlTree.children.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            Nenhum sub-módulo encontrado para este controle.
          </p>
        ) : (
          controlTree.children.map((item) => (
            <li key={item.code} className="col-span-1 rounded-lg bg-white shadow">
              <ControlTreeItem
                node={item}
                draftCodes={draftCodes}
                toggleCode={toggleCode}
                onOpenProfileModal={profileLinkedModal.handleOpen}
                onOpenAuditModal={controlAuditModal.handleOpen}
                hasUpdatePermission={hasUpdatePermission}
              />
            </li>
          ))
        )}
      </ul>

      {/* Modals */}
      <ProfileLinkedModal
        open={profileLinkedModal.open}
        onClose={profileLinkedModal.handleClose}
        control={profileLinkedModal.control}
        profiles={profileLinkedModal.profiles}
        isLoading={profileLinkedModal.isLoading}
      />

      <ControlAuditModal
        open={controlAuditModal.open}
        onClose={controlAuditModal.handleClose}
        control={controlAuditModal.control}
        audits={controlAuditModal.audits}
        isLoading={controlAuditModal.isLoading}
        currentPage={controlAuditModal.currentPage}
        onPageChange={controlAuditModal.handlePageChange}
      />

      {/* Sticky save/discard bar */}
      <AnimatePresence>
        {isDirty && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white shadow-lg"
          >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
              <p className="text-sm text-gray-600">{changeSummary}</p>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={reset}
                  disabled={saveMutation.isPending}
                >
                  <Undo2 className="mr-1.5 h-4 w-4" />
                  Descartar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="bg-black text-white hover:bg-gray-800"
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending ? (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-1.5 h-4 w-4" />
                  )}
                  {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation blocker confirmation dialog */}
      <AlertDialog open={blocker.state === 'blocked'}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alterações não salvas</AlertDialogTitle>
            <AlertDialogDescription>
              Você tem alterações que ainda não foram salvas. Se sair agora, as alterações serão
              perdidas. Deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => blocker.reset?.()}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                reset();
                blocker.proceed?.();
              }}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Descartar e sair
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Profile change confirmation dialog */}
      <AlertDialog open={pendingProfileId !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alterações não salvas</AlertDialogTitle>
            <AlertDialogDescription>
              Você tem alterações que ainda não foram salvas. Trocar de perfil irá descartar essas
              alterações. Deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingProfileId(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingProfileId !== null) {
                  reset();
                  onProfileChange(pendingProfileId);
                  setPendingProfileId(null);
                }
              }}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Descartar e trocar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
